import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Button,
  Message,
  Table,
  Icon,
  Progress,
  Header,
  Segment,
} from "semantic-ui-react";
import { goTo } from "@history";
import { BackOfficeRoutes } from "@routes/urls";
import { marc21Api } from "@api/marc21/marc21";
import { http } from "@api/base";

const MARC21ImportUploader = ({ trigger, onRecordSelected }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [errors, setErrors] = useState([]);
  const [uploadError, setUploadError] = useState(null);

  const handleOpen = () => {
    setIsOpen(true);
    setFile(null);
    setDocuments([]);
    setErrors([]);
    setUploadError(null);
  };

  const handleClose = () => {
    setIsOpen(false);
    setFile(null);
    setDocuments([]);
    setErrors([]);
    setUploadError(null);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file extension
      const fileName = selectedFile.name.toLowerCase();
      if (
        !fileName.endsWith(".mrc") &&
        !fileName.endsWith(".marc") &&
        !fileName.endsWith(".dat") &&
        !fileName.endsWith(".001")
      ) {
        setUploadError(
          "Please select a valid MARC21 file (.mrc, .marc, .dat, or .001)"
        );
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setUploadError(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setUploadError("Please select a file first");
      return;
    }

    setUploading(true);
    setUploadError(null);
    setDocuments([]);
    setErrors([]);

    try {
      const response = await marc21Api.parse(file);
      const data = response.data;

      if (data.success) {
        setDocuments(data.documents);
        setErrors(data.errors);
      } else {
        setUploadError(data.error);
      }
    } catch (error) {
      setUploadError(
        error.response?.data?.error || error.message || "Upload failed"
      );
    } finally {
      setUploading(false);
    }
  };

  const handleSelectRecord = (doc) => {
    if (onRecordSelected) {
      onRecordSelected(doc.data);
      handleClose();
    } else {
      // Store data in sessionStorage
      const dataToStore = {
        ...doc.data,
        // Ensure edition is explicitly included
        edition: doc.data.edition || "",
      };

      sessionStorage.setItem("marc21ImportData", JSON.stringify(dataToStore));

      goTo(BackOfficeRoutes.documentCreate);
      handleClose();
    }
  };

  const handleImportAll = async () => {
    if (documents.length === 0) return;

    setUploading(true);
    const results = {
      successful: [],
      failed: [],
    };

    try {
      // Import all documents one by one
      for (let i = 0; i < documents.length; i++) {
        const doc = documents[i];

        try {
          // Create the document via API
          const response = await http.post("/documents/", doc.data);

          // Extract PID from response
          const pid =
            response.data?.pid || response.data?.metadata?.pid || response.pid;

          results.successful.push({
            index: doc.index,
            title: doc.title,
            pid: pid,
          });
        } catch (error) {
          results.failed.push({
            index: doc.index,
            title: doc.title,
            error: error.response?.data?.message || error.message,
          });
        }
      }

      setUploading(false);

      if (results.failed.length > 0) {
        alert(`${results.failed.length} documents failed to import`);
      }

      handleClose();

      // Navigate to the first successfully imported document if PID exists
      if (results.successful.length > 0 && results.successful[0].pid) {
        goTo(BackOfficeRoutes.documentDetailsFor(results.successful[0].pid));
      } else if (results.successful.length > 0) {
        // If no PID, just navigate to the documents list
        goTo(BackOfficeRoutes.documentsList);
      }
    } catch (error) {
      setUploading(false);
    }
  };

  return (
    <>
      <span onClick={handleOpen} style={{ cursor: "pointer" }}>
        {trigger || <Button icon="upload" content="Import MARC21" />}
      </span>

      <Modal open={isOpen} onClose={handleClose} size="large">
        <Modal.Header>
          <Icon name="upload" />
          Import MARC21 Records
        </Modal.Header>

        <Modal.Content>
          <Segment>
            <Header as="h4">Step 1: Select MARC21 File</Header>
            <p>
              Upload a MARC21 file (.mrc, .marc, or .dat) containing
              bibliographic records.
            </p>

            <input
              type="file"
              accept=".mrc,.marc,.dat,.001" // ADD .001 HERE
              onChange={handleFileChange}
              style={{ marginBottom: "10px" }}
            />

            {file && (
              <Message info>
                <Icon name="file" />
                Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)
              </Message>
            )}

            {uploadError && (
              <Message error>
                <Message.Header>Error</Message.Header>
                <p>{uploadError}</p>
              </Message>
            )}

            <Button
              primary
              onClick={handleUpload}
              disabled={!file || uploading}
              loading={uploading}
            >
              <Icon name="search" />
              Parse File
            </Button>
          </Segment>

          {uploading && (
            <Segment>
              <Progress active color="blue">
                Parsing MARC21 file...
              </Progress>
            </Segment>
          )}

          {documents.length > 0 && (
            <Segment>
              <Header as="h4">Step 2: Select Records to Import</Header>
              <Message success>
                <Icon name="check circle" />
                Found {documents.length} record(s) in the file
              </Message>

              {errors.length > 0 && (
                <Message warning>
                  <Message.Header>Parsing Warnings</Message.Header>
                  <p>{errors.length} record(s) could not be parsed</p>
                </Message>
              )}

              <Table celled striped selectable>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell width={1}>#</Table.HeaderCell>
                    <Table.HeaderCell width={6}>Title</Table.HeaderCell>
                    <Table.HeaderCell width={2}>Edition</Table.HeaderCell>
                    <Table.HeaderCell width={4}>Authors</Table.HeaderCell>
                    <Table.HeaderCell width={2}>Year</Table.HeaderCell>
                    <Table.HeaderCell width={1}>Actions</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>

                <Table.Body>
                  {documents.map((doc, index) => (
                    <Table.Row key={index}>
                      <Table.Cell>{index + 1}</Table.Cell>
                      <Table.Cell>
                        <strong>{doc.title || "Untitled"}</strong>
                      </Table.Cell>
                      <Table.Cell>
                        {doc.data.edition ? (
                          <span style={{ fontSize: "0.9em" }}>
                            {doc.data.edition}
                          </span>
                        ) : (
                          <span style={{ color: "#999", fontSize: "0.85em" }}>
                            No edition
                          </span>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {doc.authors.length > 0 ? (
                          <div>
                            {doc.authors.slice(0, 2).map((author, i) => (
                              <div key={i} style={{ fontSize: "0.9em" }}>
                                {author.full_name}
                              </div>
                            ))}
                            {doc.authors.length > 2 && (
                              <div
                                style={{ fontSize: "0.85em", color: "#666" }}
                              >
                                +{doc.authors.length - 2} more
                              </div>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: "#999" }}>No authors</span>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {doc.data.publication_year || "-"}
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        <Button
                          size="mini"
                          primary
                          onClick={() => handleSelectRecord(doc)}
                        >
                          <Icon name="edit" />
                          Import
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>

              {documents.length > 1 && (
                <div style={{ marginTop: "15px", textAlign: "right" }}>
                  <Button color="green" onClick={handleImportAll}>
                    <Icon name="download" />
                    Import All ({documents.length} records)
                  </Button>
                </div>
              )}
            </Segment>
          )}
        </Modal.Content>

        <Modal.Actions>
          <Button onClick={handleClose}>Close</Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

MARC21ImportUploader.propTypes = {
  trigger: PropTypes.node,
  onRecordSelected: PropTypes.func,
};

MARC21ImportUploader.defaultProps = {
  trigger: null,
  onRecordSelected: null,
};

export default MARC21ImportUploader;
